import type {
  EventQueueAcknowledgement,
  EventQueueAttentionItem,
  EventQueueBackgroundJob,
  EventQueueBackgroundJobsResponse,
  EventQueueFailureGroup,
  EventQueueFailuresResponse,
  EventQueueOverviewResponse,
  EventQueueStalledJob,
  EventQueueWorkersResponse,
} from "@t3tools/contracts";

export type InboxSeverity = "critical" | "warning";

/**
 * One thing that needs a person, with what they need to act on it. Every
 * source of trouble lands here so nobody has to visit four tabs to find out
 * whether anything is wrong.
 */
export type InboxItem =
  | { item: EventQueueAttentionItem; key: string; kind: "queue"; severity: InboxSeverity }
  | { group: EventQueueFailureGroup; key: string; kind: "failures"; severity: InboxSeverity }
  | { job: EventQueueStalledJob; key: string; kind: "stalled"; severity: InboxSeverity }
  | { job: EventQueueBackgroundJob; key: string; kind: "schedule"; severity: InboxSeverity };

const SEVERITY_ORDER: Record<InboxSeverity, number> = { critical: 0, warning: 1 };
const KIND_ORDER: Record<InboxItem["kind"], number> = {
  queue: 0,
  failures: 1,
  stalled: 2,
  schedule: 3,
};

/**
 * Most urgent first: critical before warning, then queue-level problems (a
 * paused or workerless queue blocks everything behind it), then failed jobs,
 * largest group first, then stalled jobs and schedules.
 *
 * Failure groups replace the overview's per-queue "failed jobs" item, since a
 * group says what went wrong and can be fixed in one go. Until groups load,
 * the overview's item stands in so a failure never goes unshown.
 */
export function buildInbox(input: {
  failures: EventQueueFailuresResponse | undefined;
  overview: EventQueueOverviewResponse | undefined;
  schedules: EventQueueBackgroundJobsResponse | undefined;
  workers: EventQueueWorkersResponse | undefined;
}): InboxItem[] {
  const items: InboxItem[] = [];
  for (const item of input.overview?.attention ?? []) {
    if (item.kind === "failed" && input.failures) continue;
    items.push({ item, key: `queue:${item.id}`, kind: "queue", severity: item.severity });
  }
  for (const group of input.failures?.groups ?? []) {
    items.push({
      group,
      key: `failures:${group.queueName}:${group.fingerprint}`,
      kind: "failures",
      severity: "warning",
    });
  }
  for (const job of input.workers?.stalled ?? []) {
    items.push({ job, key: `stalled:${job.eventId}`, kind: "stalled", severity: "warning" });
  }
  for (const job of input.schedules?.jobs ?? []) {
    if (job.health !== "failing" && job.health !== "late") continue;
    items.push({
      job,
      key: `schedule:${job.key}`,
      kind: "schedule",
      severity: job.health === "failing" ? "critical" : "warning",
    });
  }
  return items
    .map((item, index) => ({ index, item }))
    .sort(
      (left, right) =>
        SEVERITY_ORDER[left.item.severity] - SEVERITY_ORDER[right.item.severity] ||
        KIND_ORDER[left.item.kind] - KIND_ORDER[right.item.kind] ||
        left.index - right.index,
    )
    .map(({ item }) => item);
}

/** The first line of an error, which is what a person recognizes it by. */
export function errorHeadline(message: string): string {
  const line = message.split("\n").find((part) => part.trim().length > 0) ?? message;
  return line.trim();
}

/** What an Inbox row is about, in a few words, for places that list it without its details. */
export function inboxItemTitle(item: InboxItem): string {
  switch (item.kind) {
    case "queue":
      return item.item.title;
    case "failures":
      return errorHeadline(item.group.sampleMessage);
    case "stalled":
      return `A stuck ${item.job.eventType} job`;
    case "schedule":
      return `${item.job.label} is ${item.job.health === "failing" ? "failing" : "late"}`;
  }
}

/**
 * Whether an acknowledgement still holds. It lapses when it expires, and a
 * failure group acknowledged "until it fails again" lapses once a job in it
 * fails after the acknowledgement, so a new fire is never hidden.
 */
export function acknowledgementHolds(
  item: InboxItem,
  acknowledgement: EventQueueAcknowledgement,
  now: number,
): boolean {
  if (acknowledgement.expiresAt) return Date.parse(acknowledgement.expiresAt) > now;
  if (item.kind !== "failures") return false;
  return Date.parse(item.group.lastSeenAt) <= Date.parse(acknowledgement.acknowledgedAt);
}

/** The Inbox split into what still needs a person and what someone is already on. */
export function partitionAcknowledged(
  items: readonly InboxItem[],
  acknowledgements: readonly EventQueueAcknowledgement[],
  now: number,
): {
  acknowledged: { acknowledgement: EventQueueAcknowledgement; item: InboxItem }[];
  open: InboxItem[];
} {
  const byKey = new Map(acknowledgements.map((ack) => [ack.itemKey, ack]));
  const open: InboxItem[] = [];
  const acknowledged: { acknowledgement: EventQueueAcknowledgement; item: InboxItem }[] = [];
  for (const item of items) {
    const acknowledgement = byKey.get(item.key);
    if (acknowledgement && acknowledgementHolds(item, acknowledgement, now)) {
      acknowledged.push({ acknowledgement, item });
    } else {
      open.push(item);
    }
  }
  return { acknowledged, open };
}
